import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Gift, Loader2, Copy, Share2 } from "lucide-react";
import { customerCustomFetch } from "@workspace/api-client-react";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { ConfirmNumberDialog } from "@/components/CustomerCodeStep";
import {
  chavePremio,
  diaMes,
  DIAS_SEMANA,
  fmtPontos,
  fmtPts,
  proximoPremio,
  regraDeGanho,
  type StoreLoyalty,
} from "@/lib/loyalty";

// "Pontos em <loja>" na Minha conta do link (programa de pontos do Ultra, 05/10/2026). Mostra só o que a loja
// cadastrou; saldo, extrato e prêmios ganhos só depois de confirmar o número no WhatsApp.
export function PontosDaConta({ storeSlug, storeName }: { storeSlug: string; storeName: string | null }) {
  const { customer, verified } = useCustomerAuth();
  const [, navigate] = useLocation();
  const [confirmar, setConfirmar] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const { data, isLoading, refetch } = useQuery<StoreLoyalty>({
    queryKey: ["store-loyalty", storeSlug, customer?.id ?? null, verified],
    queryFn: () => customerCustomFetch<StoreLoyalty>(`/api/catalog/${storeSlug}/loyalty`),
    enabled: !!storeSlug && !!customer,
    staleTime: 15_000,
  });

  if (isLoading) {
    return (
      <section className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando pontos...
      </section>
    );
  }
  if (!data?.enabled || !data.program) return null;
  const prog = data.program;
  const titulo = storeName ? `Pontos em ${storeName}` : "Pontos nesta loja";

  const usar = (rewardId: string) => {
    try {
      localStorage.setItem(chavePremio(storeSlug), rewardId);
    } catch {}
    navigate(`/loja/${storeSlug}`);
  };

  const comoGanhar: Array<{ nome: string; pontos: string }> = [
    { nome: regraDeGanho(prog), pontos: "" },
    ...prog.earnWays.map((w) => ({ nome: w.name, pontos: `+${w.points}` })),
    ...(prog.bonusFirstOrder > 0 ? [{ nome: "No 1º pedido", pontos: `+${prog.bonusFirstOrder}` }] : []),
    ...(prog.bonusBirthday > 0 ? [{ nome: "Na semana do seu aniversário", pontos: `+${prog.bonusBirthday}` }] : []),
    ...(prog.doubleDays.length > 0
      ? [{ nome: `Pedidos em dobro: ${prog.doubleDays.map((d) => DIAS_SEMANA[d]).join(", ")}`, pontos: "2×" }]
      : []),
  ];

  const link = data.referral ? `${window.location.origin}/loja/${storeSlug}?ind=${data.referral.code}` : null;
  const textoIndicacao = link ? `Peça na ${storeName ?? "loja"} por aqui: ${link}` : "";

  return (
    <section className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm space-y-4" data-testid="conta-pontos">
      <div className="flex items-center gap-2">
        <Gift className="w-5 h-5 text-primary" />
        <h2 className="font-semibold text-foreground">{titulo}</h2>
      </div>

      {!data.verified ? (
        <>
          <p className="text-sm text-muted-foreground">Confirme seu número para ver seus pontos e prêmios.</p>
          <button
            type="button"
            onClick={() => setConfirmar(true)}
            className="w-full rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-semibold"
            data-testid="conta-pontos-confirmar"
          >
            Confirmar pelo WhatsApp
          </button>
          <ConfirmNumberDialog open={confirmar} onOpenChange={setConfirmar} storeSlug={storeSlug} onVerified={() => void refetch()} />
        </>
      ) : (
        <>
          <div>
            <p className="flex items-baseline gap-1.5">
              <span className="text-4xl font-bold font-display tabular-nums" data-testid="conta-pontos-saldo">
                {(data.balance ?? 0).toLocaleString("pt-BR")}
              </span>
              <span className="text-muted-foreground">{(data.balance ?? 0) === 1 ? "ponto" : "pontos"}</span>
            </p>
            {data.expiring && data.expiring.length > 0 && (
              <p className="mt-1.5 inline-block text-xs font-semibold text-amber-800 bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 rounded-full px-2.5 py-0.5">
                {fmtPts(data.expiring[0]!.points)} vencem em {diaMes(data.expiring[0]!.date)}
              </p>
            )}
            {(() => {
              const prox = proximoPremio(prog.rewards, data.balance ?? 0);
              if (!prox) return null;
              const pct = Math.min(100, Math.round(((data.balance ?? 0) / prox.reward.cost) * 100));
              return (
                <div className="mt-3 space-y-1">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Faltam {fmtPts(prox.faltam)} para {prox.reward.name}
                  </p>
                </div>
              );
            })()}
          </div>

          {prog.rewards.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Prêmios</p>
              <ul className="divide-y divide-border/60">
                {prog.rewards.map((r) => {
                  const esgotado = r.stockLeft != null && r.stockLeft <= 0;
                  const alcanca = r.type === "cashback" ? (data.balance ?? 0) >= Math.max(1, r.cost) : (data.balance ?? 0) >= r.cost;
                  return (
                    <li key={r.id} className="flex items-center gap-3 py-2.5">
                      <span className={`flex-1 min-w-0 text-sm ${!alcanca || esgotado ? "text-muted-foreground" : ""}`}>{r.name}</span>
                      <span className="text-xs font-semibold text-muted-foreground tabular-nums whitespace-nowrap">
                        {r.type === "cashback"
                          ? `1 pt = R$ ${r.value.toFixed(2).replace(".", ",")}`
                          : fmtPts(r.cost)}
                      </span>
                      {esgotado ? (
                        <span className="text-xs text-muted-foreground">acabou</span>
                      ) : alcanca ? (
                        <button
                          type="button"
                          onClick={() => usar(r.id)}
                          className="text-xs font-semibold rounded-lg bg-primary text-primary-foreground px-3 py-1.5"
                          data-testid={`conta-premio-usar-${r.id}`}
                        >
                          Usar
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">faltam {r.cost - (data.balance ?? 0)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {data.lines && data.lines.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Extrato</p>
              <ul className="divide-y divide-border/60" data-testid="conta-pontos-extrato">
                {data.lines.slice(0, 20).map((l) => (
                  <li key={l.id} className="grid grid-cols-[3.5rem_1fr_auto] items-baseline gap-2 py-2 text-sm">
                    <span className={`font-bold tabular-nums ${l.points > 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}>
                      {l.points > 0 ? `+${l.points}` : `−${Math.abs(l.points)}`}
                    </span>
                    <span className="min-w-0 truncate">{l.description}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {l.open && l.expiresAt ? `vence ${diaMes(l.expiresAt)}` : diaMes(l.at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.won && data.won.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Prêmios ganhos</p>
              <ul className="space-y-1">
                {data.won.map((w, i) => (
                  <li key={`${w.at}-${i}`} className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">{w.name}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{diaMes(w.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Como ganhar</p>
        <ul className="space-y-1">
          {comoGanhar.map((c, i) => (
            <li key={i} className="flex justify-between gap-3 text-sm">
              <span className="min-w-0">{c.nome}</span>
              {c.pontos && <span className="font-semibold text-green-700 dark:text-green-400 whitespace-nowrap">{c.pontos}</span>}
            </li>
          ))}
        </ul>
      </div>

      {data.verified && link && (
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 space-y-2" data-testid="conta-indicacao">
          <p className="text-sm">
            <b>Indique e ganhe {fmtPontos(prog.referralPoints)}</b>
            <span className="block text-xs text-muted-foreground">Quando a pessoa fizer o 1º pedido pelo seu link.</span>
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card py-2 text-xs font-semibold"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link);
                  setCopiado(true);
                  setTimeout(() => setCopiado(false), 2000);
                } catch {}
              }}
            >
              <Copy className="w-3.5 h-3.5" /> {copiado ? "Copiado" : "Copiar link"}
            </button>
            <a
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25D366] text-white py-2 text-xs font-semibold"
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(textoIndicacao)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Share2 className="w-3.5 h-3.5" /> Enviar no WhatsApp
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
